import { motion } from '@/components/public/PublicMotion';
import { PublicLayout } from '@/components/public/PublicLayout';
import { PublicSeo } from '@/components/public/PublicSeo';
import { fadeInUp, staggerContainer } from '@/hooks/use-scroll-animation';
import { Heading } from '@/components/ui/heading';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck,
  Globe,
  Cookie,
  BarChart2,
  ClipboardList,
  Mail,
  Users,
  Plane,
  Lock,
  Bot,
  BadgeCheck,
  AlertCircle,
  RefreshCw,
  Building2,
  FileText,
  type LucideIcon,
} from 'lucide-react';

const rights = [
  { art: 'Art. 15', label: 'Accesso', desc: 'Ottenere conferma del trattamento e copia dei tuoi dati.' },
  { art: 'Art. 16', label: 'Rettifica', desc: 'Chiedere la correzione di dati inesatti o incompleti.' },
  { art: 'Art. 17', label: 'Cancellazione', desc: 'Chiedere la cancellazione quando i dati non sono più necessari o revochi il consenso.' },
  { art: 'Art. 18', label: 'Limitazione', desc: 'Chiedere la limitazione del trattamento in determinate circostanze.' },
  { art: 'Art. 20', label: 'Portabilità', desc: 'Ricevere i tuoi dati in formato strutturato e leggibile da dispositivo automatico.' },
  { art: 'Art. 21', label: 'Opposizione', desc: 'Opporti al trattamento basato su legittimo interesse.' },
  { art: '—', label: 'Revoca del consenso', desc: 'Revocare il consenso prestato in qualsiasi momento, senza pregiudicare i trattamenti già effettuati.' },
];

const dataCategories = [
  {
    icon: Globe,
    letter: 'a',
    title: 'Dati di navigazione',
    items: ['IP, browser, sistema operativo', 'Pagine visitate, timestamp, tempo di permanenza'],
    purpose: 'Funzionamento tecnico, sicurezza e anti-abuso',
    basis: 'Legittimo interesse (art. 6.1.f) — garantire il funzionamento tecnico, la sicurezza e la prevenzione di abusi',
    retention: '24 mesi',
  },
  {
    icon: Cookie,
    letter: 'b',
    title: 'Cookie tecnici',
    items: ['Cookie di sessione', 'Preferenze cookie'],
    purpose: 'Navigazione e funzionalità di base',
    basis: 'Legittimo interesse (art. 6.1.f) — assicurare la navigazione e le funzionalità di base del Sito',
    retention: 'Sessione o max 12 mesi',
  },
  {
    icon: BarChart2,
    letter: 'c',
    title: 'Cookie analytics (PostHog)',
    items: ['Pagine visitate', 'Percorsi di navigazione', 'Interazioni con gli elementi del Sito'],
    purpose: 'Analisi aggregata per migliorare contenuti e usabilità',
    basis: 'Consenso (art. 6.1.a)',
    retention: '12 mesi',
  },
  {
    icon: ClipboardList,
    letter: 'd',
    title: 'Modulo "Richiedi una demo"',
    items: [
      'Nome, cognome, email (obbligatori)',
      'Telefono, specializzazione, messaggio (facoltativi)',
      'Il mancato conferimento dei dati obbligatori impedisce l\'invio della richiesta',
    ],
    purpose: 'Rispondere alla richiesta e organizzare una demo',
    basis: 'Consenso (art. 6.1.a)',
    retention: "24 mesi dall'ultimo contatto",
  },
  {
    icon: Mail,
    letter: 'e',
    title: 'Email di conferma',
    items: ['Indirizzo email fornito nel modulo'],
    purpose: 'Confermare la presa in carico della richiesta',
    basis: 'Misure precontrattuali (art. 6.1.b)',
    retention: 'Vedi dati modulo demo',
  },
];

const recipients = [
  { name: 'Amazon Web Services Inc.', service: 'Infrastruttura', location: 'Unione Europea' },
  { name: 'Cloudflare Inc.', service: 'CDN e sicurezza', location: 'Stati Uniti *' },
  { name: 'Cookiebot (Usercentrics)', service: 'Gestione consenso cookie', location: 'Unione Europea' },
  { name: 'Hetzner Online GmbH', service: 'Hosting del Sito', location: 'Unione Europea' },
  { name: 'PostHog Inc.', service: 'Analytics', location: 'Unione Europea' },
  { name: 'Resend Inc.', service: 'Invio email transazionali', location: 'Stati Uniti *' },
  { name: 'Slack Technologies LLC', service: 'Notifiche operative interne', location: 'Stati Uniti *' },
];

const SectionHeader = ({
  icon: Icon,
  number,
  title,
}: {
  icon: LucideIcon;
  number: string;
  title: string;
}) => (
  <Heading level={2} icon={Icon} className="mb-5">
    <span className="text-muted-foreground mr-2">{number}</span>
    {title}
  </Heading>
);

const PrivacyPolicy = () => {
  return (
    <PublicLayout>
      <PublicSeo
        title="Emovia | Privacy Policy"
        description="Informativa sul trattamento dei dati personali raccolti attraverso il sito pubblico di Emovia."
        path="/privacy-policy"
      />

      {/* Hero */}
      <section className="py-20 md:py-28 bg-blue-lighter/30">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl mx-auto"
          >
            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }} className="mb-6">
              <Badge variant="outline" className="bg-primary/10 border-primary/20 text-primary/80 rounded-full px-4 py-1.5">
                Ultimo aggiornamento: 25 marzo 2026
              </Badge>
            </motion.div>
            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }}>
              <Heading level={1} font="display" className="text-primary mb-3">
                Informativa sul trattamento dei dati personali
              </Heading>
            </motion.div>
            <motion.p variants={fadeInUp} transition={{ duration: 0.5 }} className="text-lg text-muted-foreground">
              ai sensi degli artt. 13-14 del Regolamento (UE) 2016/679 (&ldquo;GDPR&rdquo;)
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <section className="pb-24">
        <div className="container">
          <div className="max-w-3xl mx-auto">

            {/* 1. Titolare */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Building2} number="1." title="Titolare del trattamento" />
              <div className="text-muted-foreground space-y-1">
                <p className="text-foreground">Emovia di Daniele Di Bernardo</p>
                <p>Viale Sicilia 31, 27100 Pavia (PV), Italia</p>
                <p>P.IVA: IT03023520186</p>
                <a
                  href="mailto:privacy@emovia.com"
                  aria-label="Invia email a privacy@emovia.com"
                  className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline mt-2"
                >
                  <Mail className="h-4 w-4" aria-hidden />
                  privacy@emovia.com
                </a>
              </div>
            </motion.div>

            {/* 2. Ambito */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={FileText} number="2." title="Ambito di applicazione" />
              <p className="text-muted-foreground">
                La presente informativa si applica esclusivamente al trattamento dei dati personali
                raccolti tramite il sito web <span className="text-foreground">www.emovia.com</span>.
                Per i trattamenti relativi alla piattaforma Emovia destinata ai professionisti
                e ai loro pazienti è disponibile un&apos;informativa dedicata.
              </p>
            </motion.div>

            {/* 3. Dati raccolti */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={ShieldCheck} number="3." title="Dati raccolti, finalità e basi giuridiche" />
              <div className="space-y-8">
                {dataCategories.map(({ icon: Icon, letter, title, items, purpose, basis, retention }) => (
                  <div key={title}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden />
                      <Heading level={3}>
                        <span className="text-muted-foreground mr-1.5">{letter})</span>
                        {title}
                      </Heading>
                    </div>
                    <ul className="list-disc pl-5 text-muted-foreground space-y-1 mb-4">
                      {items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                    <dl className="grid sm:grid-cols-3 gap-x-6 gap-y-2 text-sm border-t border-border/50 pt-3">
                      <div>
                        <dt className="text-muted-foreground mb-0.5">Finalità</dt>
                        <dd className="text-foreground">{purpose}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground mb-0.5">Base giuridica</dt>
                        <dd className="text-foreground">{basis}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground mb-0.5">Conservazione</dt>
                        <dd className="text-foreground">{retention}</dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* 4. Destinatari */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={Users} number="4." title="Destinatari dei dati" />
              <p className="text-muted-foreground mb-6">
                I tuoi dati possono essere comunicati ai seguenti Responsabili del trattamento (art. 28 GDPR),
                sulla base di specifici accordi (DPA). I dati non vengono venduti, ceduti o comunicati a
                terzi per finalità di marketing.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-muted/40 text-left border-b border-border">
                      <th scope="col" className="px-4 py-3 text-foreground">Fornitore</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Servizio</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Sede server</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {recipients.map(({ name, service, location }) => (
                      <tr key={name} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 text-foreground">{name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{service}</td>
                        <td className="px-4 py-3 text-muted-foreground">{location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* 5. Trasferimenti */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Plane} number="5." title="Trasferimenti verso Paesi terzi" />
              <p className="text-muted-foreground mb-4">
                I fornitori contrassegnati con (*) nella tabella precedente hanno sede negli Stati Uniti.
                I trasferimenti avvengono sulla base di:
              </p>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                  <span>
                    <span className="text-foreground">Clausole Contrattuali Standard (SCC)</span>{' '}
                    approvate dalla Commissione Europea (decisione 2021/914), incluse nei rispettivi DPA.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                  <span>
                    <span className="text-foreground">Data Processing Agreement (DPA)</span>{' '}
                    sottoscritto con ciascun fornitore.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                  <span>
                    <span className="text-foreground">EU-US Data Privacy Framework</span>{' '}
                    (decisione di adeguatezza 2023/1795), ove applicabile al fornitore.
                  </span>
                </li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Non vengono effettuati altri trasferimenti di dati personali al di fuori dello Spazio
                Economico Europeo.
              </p>
            </motion.div>

            {/* 6. Sicurezza */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={Lock} number="6." title="Misure di sicurezza" />
              <ul className="space-y-3 text-muted-foreground">
                {[
                  'Cifratura dei dati in transito e a riposo.',
                  'Accesso ai sistemi limitato al personale autorizzato.',
                  'Protezione anti-spam e misure di rate limiting sui moduli.',
                  "Monitoraggio continuo dell'infrastruttura.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <ShieldCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* 7. Decisioni automatizzate */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Bot} number="7." title="Decisioni automatizzate e profilazione" />
              <p className="text-muted-foreground">
                Il Sito non effettua alcun trattamento automatizzato finalizzato alla profilazione degli
                utenti né adotta decisioni basate unicamente su trattamenti automatizzati che producano
                effetti giuridici o incidano significativamente sulla tua persona.
              </p>
            </motion.div>

            {/* 8. Diritti */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={BadgeCheck} number="8." title="I tuoi diritti" />
              <ul className="divide-y divide-border/60 mb-6">
                {rights.map(({ art, label, desc }) => (
                  <li key={label} className="flex items-baseline gap-4 py-3">
                    <span className="text-muted-foreground text-sm w-16 flex-shrink-0">{art}</span>
                    <div>
                      <span className="text-foreground">{label}</span>
                      <p className="text-muted-foreground mt-0.5">{desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground">
                Per esercitare i tuoi diritti, scrivi a{' '}
                <a
                  href="mailto:privacy@emovia.com"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  privacy@emovia.com
                </a>{' '}
                indicando il diritto che intendi esercitare e allegando un documento di identità.
                Risponderemo entro 30 giorni dalla richiesta.
              </p>
            </motion.div>

            {/* 9. Reclamo */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={AlertCircle} number="9." title="Diritto di reclamo" />
              <p className="text-muted-foreground mb-4">
                Hai il diritto di proporre reclamo all&apos;autorità di controllo competente:
              </p>
              <div className="text-muted-foreground space-y-1">
                <p className="text-foreground">Garante per la Protezione dei Dati Personali</p>
                <p>Piazza Venezia 11, 00187 Roma</p>
                <div className="pt-2 space-y-1.5">
                  <a
                    href="https://www.garanteprivacy.it"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <Globe className="h-4 w-4" aria-hidden />
                    www.garanteprivacy.it
                  </a>
                  <a
                    href="mailto:protocollo@gpdp.it"
                    aria-label="Invia email a protocollo@gpdp.it"
                    className="flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <Mail className="h-4 w-4" aria-hidden />
                    protocollo@gpdp.it
                  </a>
                  <a
                    href="mailto:protocollo@pec.gpdp.it"
                    aria-label="Invia email a protocollo@pec.gpdp.it"
                    className="flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <Mail className="h-4 w-4" aria-hidden />
                    protocollo@pec.gpdp.it (PEC)
                  </a>
                </div>
              </div>
            </motion.div>

            {/* 10. Cookie Policy */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={Cookie} number="10." title="Cookie Policy" />
              <p className="text-muted-foreground">
                Per informazioni dettagliate sui cookie utilizzati dal Sito, sulle modalità di gestione
                delle preferenze e su come disabilitarli, consulta il banner di gestione cookie
                accessibile in qualsiasi momento tramite il link{' '}
                <span className="text-foreground">&ldquo;Gestisci cookie&rdquo;</span> presente nel
                footer del Sito. La gestione del consenso è affidata a CookieBot (Usercentrics).
              </p>
            </motion.div>

            {/* 11. Modifiche */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10"
            >
              <SectionHeader icon={RefreshCw} number="11." title="Modifiche alla presente informativa" />
              <p className="text-muted-foreground">
                Il Titolare si riserva di aggiornare la presente informativa per adeguarla a eventuali
                modifiche normative o a cambiamenti nei trattamenti effettuati. La data di ultimo
                aggiornamento è indicata in cima al documento. In caso di modifiche sostanziali,
                ne daremo comunicazione tramite il Sito.
              </p>
            </motion.div>

          </div>

          {/* Contatti privacy */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto mt-10"
          >
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
              <Mail className="h-7 w-7 text-primary mx-auto mb-4" aria-hidden />
              <Heading level={3} className="mb-2">Hai domande sulla tua privacy?</Heading>
              <p className="text-muted-foreground mb-5">
                Scrivici. Ti rispondiamo entro 30 giorni.
              </p>
              <a
                href="mailto:privacy@emovia.com"
                aria-label="Invia email a privacy@emovia.com"
                className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
              >
                <Mail className="h-4 w-4" aria-hidden />
                privacy@emovia.com
              </a>
            </div>
          </motion.div>

        </div>
      </section>
    </PublicLayout>
  );
};

export default PrivacyPolicy;
